import * as yup from "yup";
import { InstallationType } from "components/EtudeForm/hooks/useGetSolutionsQuery";
import Button from "components/shared/button";
import { Text } from "components/shared/text";
import SwitchableCustom from "components/SwitchableCustom";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import useTranslation from "utils/hooks/useTranslation";
import { areArraysEqual } from "utils";
import { useGetSolutions } from "../../hooks/useGetSolutions";
import { FilterCheckboxGroup, FilterInput } from "./FilterItems";
import { useAppliedFilterValue } from "./useAppliedFilterValue";
import {
  BOILER_TYPE_GROUND,
  BOILER_TYPE_WALL,
} from "utils/helpers/boilerType";
import {
  FilterComponentType,
  FilterItemConfig,
  objectifOptions,
  useFilterConfig,
} from "./useFilterConfig";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import { message } from "antd";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCurrentEtudeData } from "components/EtudeForm/hooks/useCurrentEtudeData";
import { usePopup } from "providers/PopupProvider";
import { ModifyParamsPopup } from "./ModifyParamsPopup";

export const SolutionFilters: React.FC<{
  type?: InstallationType;
  appliedSolutionFilter?: GetSolutionRequest | null;
  service?: string;
  hasEcs?: boolean;
  hasChauffage?: boolean;
  hasTcoupure?: boolean;
}> = ({ type, appliedSolutionFilter, service, hasEcs, hasChauffage, hasTcoupure }) => {
  const { t } = useTranslation();
  const { network } = useSalesNetwork();
  const { onFilterSolutions } = useGetSolutions();
  const { activeSolutions } = useCurrentEtudeData();
  const { openPopup } = usePopup();
  const [validationSchema, setValidationSchema] = useState<any>({});
  const hasSyncedInitialBoilerType = useRef(false);
  const hasShownUpdateMessage = useRef(false);

  const { defaultValues, traitement_anti_corrosion } = useAppliedFilterValue({
    appliedSolutionFilter,
    appointPuissanceTotale: activeSolutions?.[0]?.appoint?.puissance_totale,
  });

  const { control, setValue, getValues, reset, watch } = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const [preference_pac, code_gamme_pac, taux_couverture, part_pac] = watch([
    "preference_pac",
    "code_gamme_pac",
    "taux_couverture",
    "part_pac",
  ]);

  const filterConfig = useFilterConfig({
    appliedSolutionFilter,
    type,
    code_gamme_pac,
    traitement_anti_corrosion,
    preference_pac,
    hasEcs,
    hasChauffage,
    service,
    hasTcoupure,
    appointPuissanceTotale: activeSolutions?.[0]?.appoint?.puissance_totale,
  });

  useEffect(() => {
    const fields: Record<string, any> = {};
    filterConfig.forEach((f) => {

      if (f.displayCondition && (f.min != undefined || f.max != undefined)) {
        const title = t(f.title);
        const errorMessage = t(f.errorMessage || "", [title, f.min, f.max]);

        // f.max != undefined && f.min != undefined
        //   ? t("FO_FILTER_BETWEEN_VALUE", [title, f.min, f.max])
        //   : f.min != undefined
        //   ? t("FO_FILTER_MIN_VALUE", [title, f.min])
        //   : f.max != undefined
        //   ? t("FO_FILTER_MAX_VALUE", [title, f.max])
        //   : "";
        if (f.required) {
          fields[f.name] = yup.number().transform((v, o) => (o === "" ? undefined : v)).required(errorMessage);
        } else {
          fields[f.name] = yup.number().nullable();
        }
        if (f.min != undefined) {
          fields[f.name] = fields[f.name].min(f.min, errorMessage);
        }
        if (f.max != undefined) {
          fields[f.name] = fields[f.name].max(f.max, errorMessage);
        }

      }
    });
    setValidationSchema(yup.object().shape(fields));
  }, [t]);
  const onFilter = useCallback(() => {
    const {
      code_gamme_pac,
      preference_pac,
      hauteur_porte,
      largeur_porte,
      objectif,
      type_chaudiere,
      ...rest
    } = getValues();

    const boilerTypeConfig = filterConfig.find(
      (filter) => filter.name === "type_chaudiere"
    );
    const allowedBoilerTypes = (boilerTypeConfig?.options ?? [])
      .filter((option) => !option.disabled)
      .map((option) => option.value);

    const selectedBoilerTypes: string[] = Array.isArray(type_chaudiere)
      ? type_chaudiere.filter((v) => allowedBoilerTypes.includes(v))
      : typeof type_chaudiere === "string" &&
        allowedBoilerTypes.includes(type_chaudiere)
        ? [type_chaudiere]
        : [];
    // Build filters object with logic for displayCondition and recalculateWhenRemoved
    const filters = Object.fromEntries(
      Object.entries(rest)
        .map(([key, value]) => {
          const config = filterConfig.find((f) => f.name === key);
          // If displayCondition is false and recalculateWhenRemoved is true
          if (
            config &&
            config.recalculateWhenRemoved &&
            !config.displayCondition
          ) {
            // If defaultValue is not specified, omit this filter
            if (
              config.defaultValue === undefined ||
              config.defaultValue === null
            ) {
              return null;
            }
            return [key, config.defaultValue];
          }
          return [key, value];
        })
        .filter((entry) => {
          if (!entry) return false;
          const [key, value] = entry;
          // Exclude filters with null, empty string, or undefined values
          const isNotEmpty = ![null, "", undefined].includes(value);
          // Only include filters where displayCondition is not explicitly false
          const isDisplayable =
            filterConfig.find((f) => f.name === key)?.displayCondition !==
            false;
          // Exclude keys that are present in objectifOptions
          const isNotObjectifValue = !objectifOptions?.some(
            (ob) => ob.value == key
          );
          return isNotEmpty && isDisplayable && isNotObjectifValue;
        }) as Array<[string, any]>
    );

    // Handle objectif for hybride
    if (
      type === InstallationType.hybride &&
      objectif &&
      filterConfig.find((f) => f.name === "objectif")?.displayCondition
    ) {
      filters[objectif] = true;
    }

    // Handle preference_pac
    if (preference_pac?.length) filters.preference_pac = preference_pac;
    const codeGammePacConfig = filterConfig.find(
      (f) => f.name === "code_gamme_pac"
    );
    let codeGammePacValue = code_gamme_pac;
    if (codeGammePacConfig && !codeGammePacConfig?.displayCondition) {
      codeGammePacValue = codeGammePacConfig.defaultValue;
    }

    if (appliedSolutionFilter) {
      onFilterSolutions({
        ...appliedSolutionFilter,
        hauteur_porte: hauteur_porte || undefined,
        largeur_porte: largeur_porte || undefined,
        code_gamme_pac: codeGammePacValue,
        filtres: {
          ...filters,
          ...(type === InstallationType.hybride && network?.displayResultBoilerType
            ? {
              type_chaudiere: selectedBoilerTypes,
            }
            : {}),
        },
      });
      reset(getValues(), { keepValues: true });
    }
  }, [
    getValues,
    service,
    onFilterSolutions,
    appliedSolutionFilter,
    type,
    filterConfig,
  ]);

  const hasRemovedFilters = (key: string, value: any): boolean => {
    const config = filterConfig.find((f) => f.name === key);

    if (!config?.recalculateWhenRemoved || config?.displayCondition) {
      return false;
    }

    // Check if value differs from defaultValue
    const hasDefaultValue = config.defaultValue !== undefined && config.defaultValue !== null;

    if (!hasDefaultValue) {
      return ![null, "", undefined].includes(value as string | null | undefined);
    }

    // An empty selection is not a removed filter.
    if (Array.isArray(value) && value.length === 0) {
      return false;
    }

    if (Array.isArray(value) && Array.isArray(config.defaultValue)) {
      return !areArraysEqual(value, config.defaultValue);
    }

    return value !== config.defaultValue;
  };

  useEffect(() => {
    // If filters were removed from the network, recalculate with default values from config
    if (!network || !defaultValues) {
      hasShownUpdateMessage.current = false;
      return;
    }

    const removedFilters = Object.entries(defaultValues)
      .filter(([key, value]) => hasRemovedFilters(key, value))
      .map(([key, value]) => ({
        name: key,
        currentValue: value,
        defaultValue: filterConfig.find((filter) => filter.name === key)?.defaultValue,
        displayCondition: filterConfig.find((filter) => filter.name === key)?.displayCondition,
        recalculateWhenRemoved: filterConfig.find((filter) => filter.name === key)?.recalculateWhenRemoved,
      }));

    const shouldRecalculate = removedFilters.length > 0;

    if (!shouldRecalculate) {
      hasShownUpdateMessage.current = false;
      return;
    }

    if (!hasShownUpdateMessage.current) {
      console.info("[SolutionFilters] FO_POPUP_UPDATE triggered", {
        reason:
          "A filter is hidden by the network, marked for recalculation, and has a non-default value.",
        condition: {
          displayCondition: false,
          recalculateWhenRemoved: true,
          valueDiffersFromDefault: true,
        },
        network,
        removedFilters,
        defaultValues,
      });
      hasShownUpdateMessage.current = true;
      message.info(t("FO_POPUP_UPDATE"));
      onFilter();
    }
  }, [network, defaultValues, filterConfig, onFilter, t]);



  useEffect(() => {
    if (hasSyncedInitialBoilerType.current) {
      return;
    }

    const boilerTypeConfig = filterConfig.find(
      (filter) => filter.name === "type_chaudiere"
    );
    const allowedBoilerTypes = (boilerTypeConfig?.options ?? [])
      .filter((option) => !option.disabled)
      .map((option) => option.value);

    if (allowedBoilerTypes.length !== 1) {
      return;
    }
    const currentValue = getValues("type_chaudiere");
    const selectedBoilerTypes: string[] = Array.isArray(currentValue)
      ? currentValue.filter((v) => allowedBoilerTypes.includes(v))
      : typeof currentValue === "string" &&
        allowedBoilerTypes.includes(currentValue)
        ? [currentValue]
        : [];

    const hasGround = selectedBoilerTypes.includes(BOILER_TYPE_GROUND);
    const hasWall = selectedBoilerTypes.includes(BOILER_TYPE_WALL);

    if (!hasGround && !hasWall) {
      setValue("type_chaudiere", [allowedBoilerTypes[0]], {
        shouldDirty: false,
        shouldValidate: false,
      });
      onFilter();
    }

    hasSyncedInitialBoilerType.current = true;
  }, [filterConfig, getValues, onFilter, setValue]);

  const setCheckboxValue = useCallback(
    (name: string, value: any) => {
      if (!filterConfig.find(f => f.name == name as any)?.required) {
        setValue(name, value, { shouldDirty: true, shouldValidate: true });
        onFilter();

      } else {
        if ((name === "taux_couverture" && part_pac) || (name === "part_pac" && taux_couverture)) {
          setValue(name, value, { shouldDirty: false, shouldValidate: false });
          onFilter();
          return;
        }

        setValue(name, value, { shouldDirty: true, shouldValidate: true });
      }
    },
    [onFilter, setValue, filterConfig]
  );
  const onResetField = (name: string) => {
    setCheckboxValue(name, "");
    // onFilter(); the calculation is done on setCheckboxValue if not required
  };
  const renderItem = (item: FilterItemConfig) => {
    const value = watch(item.name);
    const displayValue =
      item.name === "type_chaudiere" && Array.isArray(value)
        ? value.filter((selectedValue) =>
          (item.options ?? [])
            .filter((option) => !option.disabled)
            .some((option) => option.value === selectedValue)
        )
        : value;

    if (item.component === FilterComponentType.Input) {
      return (
        <FilterInput
          key={item.name}
          title={item.title}
          name={item.name}
          type={item.type}
          unit={item.unit}
          min={item.min}
          max={item.max}
          control={control}
          appliedValue={
            defaultValues?.[item.name as any as keyof typeof defaultValues]
          }
          isDefaultExpanded={
            !!defaultValues?.[item.name as any as keyof typeof defaultValues]
          }
          onApply={() => onFilter()}
          onReset={() => onResetField(item.name)}
        />
      );
    }

    if (item.component === FilterComponentType.CheckboxGroup) {
      return (
        <FilterCheckboxGroup
          key={item.name}
          title={item.title}
          value={displayValue}
          onChange={(v: any) => setCheckboxValue(item.name, v)}
          options={item.options}
          singleOption={item.singleOption}
          preventUncheckAll={item.preventUncheckAll}
          isDefaultExpanded={
            displayValue !== null &&
            displayValue !== undefined &&
            displayValue !== "" &&
            !(Array.isArray(displayValue) && displayValue.length == 0)
          }
        />
      );
    }

    return null;
  };

  const renderFilters = (filterNames: string[]) =>
    filterConfig
      .filter((f) => filterNames.includes(f.name) && f.displayCondition)
      .map(renderItem);

  return (
    <SwitchableCustom
      data-pointer
      defaultExpanded
      icon={
        <Button
          data-color="grey-color-mid"
          micro
          rounded
          icon="chevron_right"
          toggleClassName="rotate"
        />
      }
      title={t("FO_FILTER_ADVANCED")}
      data-index="3"
    >
      <>
        <group data-space="20" data-gap="10">
          <group
            data-type="grid"
            data-grid-template="240"
            data-gap="5"
          //data-text-size="12"
          >
            <Button
              primary
              // large
              icon="edit"
              data-width="auto"
              text={t("FO_RESULT_MODIFY_INPUT")}
              onClick={() => openPopup(<ModifyParamsPopup />)}
            />
            <group data-align="center" data-wrap="no">
              <group>
                <Text data-text-size="22">FO_FILTER_FILTER_YOUR_STUDY</Text>
              </group>
            </group>
            <group data-gap="5" data-direction="column">
              <group>
                <Text data-text-size="14" data-color="grey-color-mid">
                  FO_FILTER_PERFORMANCE
                </Text>
              </group>
              {renderFilters([
                "taux_couverture",
                "part_pac",
                "appoint",
                "objectif",
              ])}
              <separator data-horizontal="" data-margin="none"></separator>
            </group>

            <group data-gap="5" data-direction="column">
              <group>
                <Text data-text-size="14" data-color="grey-color-mid">
                  FO_FILTER_HP
                </Text>
              </group>

              {renderFilters([
                "code_gamme_pac",
                "nombre_de_pac",
                "preference_pac",
                "charge_acoustique",
                "confort_acoustique",
              ])}
              <separator data-horizontal="" data-margin="none"></separator>
            </group>

            <group data-gap="5" data-direction="column">
              <group>
                <Text data-text-size="14" data-color="grey-color-mid">
                  FO_FILTER_INSTAL_CONSTRAINTS
                </Text>
              </group>
              {renderFilters([
                "abonnement_elec",
                "hauteur_porte",
                "largeur_porte",
                "type_chaudiere",
              ])}
              <separator data-horizontal="" data-margin="none"></separator>
            </group>
          </group>
        </group>
      </>
    </SwitchableCustom>
  );
};
